import React, { useEffect, useState, useMemo } from 'react';

import PropTypes from 'prop-types';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { Button, Col, Layout, Row, Select } from 'antd';
import ButtonAdd from '../../components/ButtonAdd/ButtonAdd';
import ModalCreateStage from './ModalCreateStage';
import FinplanStegeItem from './FinplanStegeItem';
import ThreePositionSwitcher from '../../components/ThreePositionSwitcher/ThreePositionSwitcher';
import { Bubble } from '../../components/charts/Bubble/Bubble';
import { BarColumnBasic } from '../../components/charts/BarColumnBasic/BarColumnBasic';
import dayjs from 'dayjs';

import { ReactComponent as EditIcon } from '../../assets/edit-icon.svg';
import CustomSelect from '../../components/CustomSelect/CustomSelect';
import {
  createPortfolioTaskProspecting,
  clearPortfolioProspecting,
} from '../../data/store/portfolio/portfolioActions';
import Spinner from '../Model/Spiner/Sriner';

const { Header } = Layout;

const FinancialPlanStages = ({
  finplanId,
  t,
  userId,
  financialPlan,
  openCreateStageModal,
  setOpenCreateStageModal,
}) => {
  const { i18n } = useTranslation('');
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();

  const category = useSelector(
    (state) => state.accountReducer.financialCategory,
  );
  const { isLoading, analyzedPortfolio, portfolioList: { list: portfolio } } = useSelector(
    (state) => state.portfolioReducer,
  );

  const modeList = ['LIST', 'TIMELINE', 'WEALTH_APPRECIATION'];
  const [viewMode, setViewMode] = useState(modeList[0]);
  const [selectedPortfolioId, setSelectedPortfolioId] = useState(null);

  const language = i18n.languages?.length ? i18n.languages[0] : 'en';
  const currentLang = ['en', 'sw'].includes(language) ? language : 'en';

  const searchParams = new URLSearchParams(location.search);
  const itemType = searchParams.get('item-type');
  const view_mode = searchParams.get('view-mode');
  const filter = searchParams.get('filter');

  useEffect(() => {
    if (!itemType || !view_mode) {
      searchParams.set('item-type', 'expenses');
      searchParams.set('view-mode', 'LIST');
      searchParams.set('filter', 'necessity');
      history.replace({
        pathname: location.pathname,
        search: searchParams.toString(),
      });
    }
  }, [location.search, location.pathname, history]);

  useEffect(() => {
    if (view_mode) {
      setViewMode(view_mode);
    }
  }, [view_mode]);

  useEffect(() => {
    return () => {
      dispatch(clearPortfolioProspecting());
    };
  }, [dispatch]);

  const getChartData = () => {
    return (
      analyzedPortfolio?.wealth_appreciation?.map((i) => [
        i?.year,
        i?.value,
        {
          return_amount: i?.return_amount,
          deposit_fee_amount: i?.deposit_fee_amount,
          product_fee_amount: i?.product_fee_amount,
          advisory_fee_amount: i?.advisory_fee_amount,
          deposit_amount: i?.deposit_amount,
          withdrawal_amount: i?.withdrawal_amount,
          tax_amount: i?.tax_amount,
        },
      ]) ?? []
    );
  };

  const closeCreateModal = () => {
    setOpenCreateStageModal(null);
  };

  const goToPlanInfo = () => {
    history.push(`/accounts/${userId}/lifetiming/${finplanId}`);
  };

  const allExpensesData = useMemo(() => {
    if (!financialPlan?.stages) return [];

    return financialPlan.stages.reduce((acc, stage) => {
      const stageExpenses = stage.expenses || [];
      return [...acc, ...stageExpenses];
    }, []);
  }, [financialPlan]);

  const bubbleChartBounds = useMemo(() => {
    const currentYear = dayjs().year();
    const planEndYear =
      financialPlan?.end_year ||
      Math.max(
        ...(financialPlan?.stages?.map((stage) => stage.end_year) || [
          currentYear,
        ]),
      );

    return {
      startYear: currentYear,
      endYear: planEndYear,
    };
  }, [financialPlan]);

  const handleSelect = (portfolioId) => {
    setSelectedPortfolioId(portfolioId);

    const defaultSettings = {
      include_financial_plan: true,
      monetary_basis: 2,
      fee_flags: {
        include_product_fees: false,
        include_deposit_fees: false,
        include_advisory_fees: false,
      },
      withdrawal_amount: null,
      deposit_amount: null,
      reserved_amount: 0,
    };

    dispatch(
      createPortfolioTaskProspecting({
        portfolioId: portfolioId,
        data: defaultSettings,
      }),
    );
  };

  
  useEffect(() => {
    if (viewMode === 'WEALTH_APPRECIATION') {
      dispatch(clearPortfolioProspecting());
      const composite = portfolio?.find(i => i.is_composite);
      console.log('🚀 ~ FinancialPlanStages ~ composite:', composite);
      if (composite) {
        handleSelect(composite.id);
      }
    }
  }, [viewMode, dispatch]);
  
  const getStageId = (itemId) => {
    return financialPlan?.stages?.find((stage) =>
      stage?.expenses?.some((item) => item.id === itemId),
    )?.id;
  };

  const onBubbleDoubleClick = (itemId, globalCategory) => {
    const params = new URLSearchParams(location.search);
    params.set('item', itemId);
    params.set('global-category', globalCategory);
    params.set('view-type', 'table');
    history.push({
      pathname: `${location.pathname}/${getStageId(itemId)}`,
      search: `?${params.toString()}`,
    });
  };

  const handleViewMode = (mode) => {
    setViewMode(mode);
    const params = new URLSearchParams(location.search);
    params.set('view-mode', mode);
    history.push({
      pathname: location.pathname,
      search: `?${params.toString()}`,
    });
  };

  const handleFilter = (filter) => {
    const params = new URLSearchParams(location.search);
    params.set('filter', filter);
    history.push({
      pathname: location.pathname,
      search: `?${params.toString()}`,
    });
  };

  return (
    <div className="finplan" style={{ position: 'relative' }}>
      <Header className="headerStyle">
        <Row className="overview-tabs-wrapper">
          <Col className="tabs-tab-wrapper" span={12}>
            <h1 className="suggested-portfolios-title header-plan">
              {t('FINANCIAL_PLAN')}
            </h1>
            <span className="header-plan-name" onClick={goToPlanInfo}>
              {financialPlan?.name}
            </span>
          </Col>
          <Col span={12} className="action-btn-wrapper" style={{paddingRight: '48px'}}>
            <ThreePositionSwitcher
              positions={modeList}
              selected={viewMode}
              setSelected={handleViewMode}
            />
            <ButtonAdd
              title="EDIT_FINANCIAL_PLAN"
              height="32px"
              className="edit-financial-plan"
              onClick={goToPlanInfo}
            />
            <ButtonAdd
              disabled={financialPlan?.stages?.length >= 5}
              title="ADD_LIFE_STAGE"
              height="32px"
              onClick={() => setOpenCreateStageModal('new')}
            />
          </Col>
        </Row>
      </Header>
      <div className="finplan-stages-container">
        <div className="finplan-stages-label">
          {/* {viewMode === 'LIST' &&
            category?.[itemType]?.map(({ id, name }) => {
              return (
                <div key={id} className="finplan-stages-label-item">
                  <span className="vertical-bottom-to-top">
                    {t(name[currentLang])}
                  </span>
                  <div className={`fp-dot-${id}`} />
                </div>
              );
            })} */}
        </div>
        {viewMode === 'LIST' && (
          <div className="finplan-stages-list-wrapper">
            {financialPlan?.stages
              ?.sort((a, b) => a.start_year - b.start_year)
              ?.map((stage, index) => {
                return (
                  category &&
                  stage && (
                    <FinplanStegeItem
                      key={stage.id}
                      accountId={userId}
                      stage={stage}
                      count={financialPlan?.stages?.length}
                      setOpenCreateStageModal={setOpenCreateStageModal}
                      currentLang={currentLang}
                      categoryObj={category}
                      isLast={index === financialPlan?.stages?.length - 1}
                    />
                  )
                );
              })}
          </div>
        )}
        {viewMode === 'TIMELINE' && (
          <div className="finplan-stages-bubble-wrapper">
            <div className="finplan-stageitem-label-bubble">
              {financialPlan?.stages
                ?.sort((a, b) => a.start_year - b.start_year)
                ?.map((stage) => (
                  <React.Fragment key={stage.id}>
                    <div className="finplan-stageitem-label-row">
                      <div className="finplan-stageitem-label">
                        {stage?.name}{' '}
                        <span>{`${stage?.start_year}-${stage?.end_year}`}</span>
                      </div>
                    </div>
                    <Button
                      type="text"
                      icon={<EditIcon />}
                      className="finplan-stageitem-btn-edit"
                      onClick={() => setOpenCreateStageModal(stage?.id)}
                    />
                  </React.Fragment>
                ))}
            </div>
            <div className="finplan-filter-wrapper">
              <div className="finplan-filter-button">
                <div
                  className={`finplan-filter-button-item ${filter === 'necessity' ? 'selected' : ''}`}
                  onClick={() => handleFilter('necessity')}
                >
                  {t('NECESSITY')}
                </div>
                <div
                  className={`finplan-filter-button-item ${filter === 'purpose_happiness' ? 'selected' : ''}`}
                  onClick={() => handleFilter('purpose_happiness')}
                >
                  {t('PURPOSE_HAPPINESS')}
                </div>
              </div>
            </div>
            {allExpensesData.length > 0 ? (
              <div className="bubble-chart-container">
                <Bubble
                  data={allExpensesData?.filter((item) =>
                    filter === 'necessity'
                      ? item.global_category !== 'lifestyle_experiences'
                      : item.global_category === 'lifestyle_experiences',
                  )}
                  startYear={bubbleChartBounds.startYear}
                  endYear={financialPlan.year}
                  componentType="FinancialPlanStages"
                  grid={false}
                  itemsType="expenses"
                  onBubbleDoubleClick={onBubbleDoubleClick}
                />
              </div>
            ) : (
              <div className="no-data-message">{t('NO_EXPENSES_DATA')}</div>
            )}
          </div>
        )}
        {viewMode === 'WEALTH_APPRECIATION' && (
          <div className="finplan-stages-wealth-wrapper">
            <div className="finplan-stageitem-label-bubble">
              {financialPlan?.stages
                ?.sort((a, b) => a.start_year - b.start_year)
                ?.map((stage) => (
                  <React.Fragment key={stage.id}>
                    <div className="finplan-stageitem-label-row">
                      <div className="finplan-stageitem-label">
                        {stage?.name}{' '}
                        <span>{`${stage?.start_year}-${stage?.end_year}`}</span>
                      </div>
                    </div>
                    <Button
                      type="text"
                      icon={<EditIcon />}
                      className="finplan-stageitem-btn-edit"
                      onClick={() => setOpenCreateStageModal(stage?.id)}
                    />
                  </React.Fragment>
                ))}
            </div>
            <Row
              style={{ width: '100%', marginTop: '24px', marginBottom: '24px' }}
            >
              <Col span={12}>
                <CustomSelect
                  className="custom-select"
                  placeholder={t('SELECT_PORTFOLIO')}
                  type="select"
                  value={selectedPortfolioId}
                  onSelect={handleSelect}
                >
                  {portfolio?.slice()?.map((i) => {
                    return (
                      <Select.Option key={i.id} value={i.id}>
                        {i.name}
                      </Select.Option>
                    );
                  })}
                </CustomSelect>
              </Col>
            </Row>
            <div style={{ minHeight: '300px', position: 'relative' }}>
              {isLoading && selectedPortfolioId ? (
                <Spinner position="absolute" width="100%" height="100%" />
              ) : (
                <>
                  <BarColumnBasic data1={getChartData()} />
                </>
              )}
            </div>
          </div>
        )}
      </div>
      <ModalCreateStage
        t={t}
        closeCreateModal={closeCreateModal}
        open={openCreateStageModal}
        planId={+finplanId}
        currentLang={currentLang}
        financialPlan={financialPlan}
        category={category}
      />
    </div>
  );
};

export default FinancialPlanStages;

FinancialPlanStages.propTypes = {
  t: PropTypes.func,
  financialPlan: PropTypes.object,
  finplanId: PropTypes.string,
  userId: PropTypes.string,
  openCreateStageModal: PropTypes.string,
  setOpenCreateStageModal: PropTypes.func,
};
